import { Menu, Monitor, Moon, Sun } from 'lucide-react';
import * as React from 'react';
import { cn } from '../cn';
import { Drawer, DrawerContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './overlays';
import { Button } from './primitives';

// ---------------------------------------------------------------------------
// Theme (light / dark / system), class-based on <html>
// ---------------------------------------------------------------------------

export type Theme = 'light' | 'dark' | 'system';
type ThemeCtx = { theme: Theme; resolved: 'light' | 'dark'; setTheme: (t: Theme) => void };
const ThemeContext = React.createContext<ThemeCtx | null>(null);
const STORAGE_KEY = 'bb-theme';

function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function ThemeProvider({ children, defaultTheme }: { children: React.ReactNode; defaultTheme?: Theme }) {
  const [theme, setThemeState] = React.useState<Theme>(() => defaultTheme ?? readStoredTheme());
  const [systemDark, setSystemDark] = React.useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches,
  );

  React.useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved: 'light' | 'dark' = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;
  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }, [resolved]);

  const setTheme = React.useCallback((t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // storage unavailable (private mode); theme still applies for this session
    }
  }, []);

  const value = React.useMemo(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

export function ThemeToggle() {
  const { theme, setTheme, resolved } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Theme: ${theme}`}>
          {resolved === 'dark' ? <Moon /> : <Sun />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem icon={<Sun />} onSelect={() => setTheme('light')}>
          Light
        </DropdownMenuItem>
        <DropdownMenuItem icon={<Moon />} onSelect={() => setTheme('dark')}>
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem icon={<Monitor />} onSelect={() => setTheme('system')}>
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Sidebar layout (app shell)
// ---------------------------------------------------------------------------

export type SidebarNavItem = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
  badge?: React.ReactNode;
  /** Render the link (e.g. a router <Link>); receives className + children. */
  render: (props: { className: string; children: React.ReactNode; onNavigate: () => void }) => React.ReactNode;
};

export function SidebarLayout({
  header,
  nav,
  footer,
  topbar,
  children,
}: {
  /** Top of the sidebar (logo, workspace switcher). */
  header?: React.ReactNode;
  nav: SidebarNavItem[];
  /** Bottom of the sidebar (user menu). */
  footer?: React.ReactNode;
  /** Right side of the top bar (search, notifications, theme). */
  topbar?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const close = React.useCallback(() => setMobileOpen(false), []);

  const navList = (
    <nav aria-label="Main" className="flex flex-1 flex-col gap-px px-3 py-2">
      {nav.map((item) => (
        <React.Fragment key={item.key}>
          {item.render({
            onNavigate: close,
            className: cn(
              'flex h-9 items-center gap-3 rounded-[10px] px-3 text-[14px] font-medium transition-colors [&_svg]:size-[18px] [&_svg]:shrink-0',
              item.active
                ? 'bg-foreground/[0.07] text-foreground [&_svg]:text-accent-foreground'
                : 'text-sidebar-foreground hover:bg-foreground/[0.04] hover:text-foreground [&_svg]:text-muted-foreground',
            ),
            children: (
              <>
                {item.icon}
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge}
              </>
            ),
          })}
        </React.Fragment>
      ))}
    </nav>
  );

  const sidebar = (
    <div className="flex h-full flex-col">
      {header ? <div className="px-3 pb-2 pt-4">{header}</div> : null}
      <div className="flex-1 overflow-y-auto">{navList}</div>
      {footer ? <div className="px-3 pb-4 pt-2">{footer}</div> : null}
    </div>
  );

  return (
    <div className="flex min-h-dvh bg-background">
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 bg-sidebar lg:block">{sidebar}</aside>
      <Drawer open={mobileOpen} onOpenChange={setMobileOpen}>
        <DrawerContent side="left" width="sm" title="Menu" className="bg-sidebar p-0 [&>div:nth-child(2)]:p-0">
          {sidebar}
        </DrawerContent>
      </Drawer>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 bg-background/75 px-4 backdrop-blur-xl backdrop-saturate-150 sm:px-6 lg:px-10">
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
            <Menu />
          </Button>
          <div className="flex flex-1 items-center justify-end gap-1">{topbar}</div>
        </header>
        <main className="flex-1 px-4 pb-16 pt-4 sm:px-6 lg:px-10">{children}</main>
      </div>
    </div>
  );
}

/** Centered card layout for auth and portal-entry pages. */
export function AuthLayout({ logo, title, description, children, footer }: {
  logo?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-[420px] space-y-8">
        {logo ? <div className="flex justify-center">{logo}</div> : null}
        <div className="rounded-2xl bg-card p-7 shadow-card sm:p-10">
          <div className="mb-8 space-y-2 text-center">
            <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.025em]">{title}</h1>
            {description ? <p className="text-[15px] text-muted-foreground">{description}</p> : null}
          </div>
          {children}
        </div>
        {footer ? <div className="text-center text-sm text-muted-foreground">{footer}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top bar layout (app shell): brand + section links + actions in one white bar, content below on
// the paper background. Below lg the links move into a drawer behind a menu button.
// ---------------------------------------------------------------------------

export function TopNavLayout({
  brand,
  nav,
  actions,
  children,
}: {
  /** Left of the bar (logo, workspace switcher). */
  brand?: React.ReactNode;
  nav: SidebarNavItem[];
  /** Right of the bar (theme, account). */
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const close = React.useCallback(() => setMobileOpen(false), []);

  const links = (vertical: boolean) =>
    nav.map((item) => (
      <React.Fragment key={item.key}>
        {item.render({
          onNavigate: close,
          className: cn(
            'flex items-center gap-2 whitespace-nowrap rounded-lg text-[14px] transition-colors [&_svg]:shrink-0',
            vertical ? 'h-11 px-3 [&_svg]:size-[18px]' : 'h-9 px-3 [&_svg]:hidden',
            item.active
              ? 'bg-background font-semibold text-foreground'
              : 'font-medium text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
          ),
          children: (
            <>
              {item.icon}
              <span className={cn(vertical && 'flex-1')}>{item.label}</span>
              {item.badge}
            </>
          ),
        })}
      </React.Fragment>
    ));

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-30 bg-card/95 shadow-[0_1px_0_var(--color-border)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1360px] items-center gap-3 px-4 sm:px-8 lg:gap-6">
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" onClick={() => setMobileOpen(true)}>
            <Menu />
          </Button>
          {brand ? <div className="min-w-0 shrink-0">{brand}</div> : null}
          <nav aria-label="Main" className="scroll-x-hidden hidden items-center gap-1 lg:flex">
            {links(false)}
          </nav>
          <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>
        </div>
      </header>
      <Drawer open={mobileOpen} onOpenChange={setMobileOpen}>
        <DrawerContent side="left" width="sm" title="Menu">
          <nav aria-label="Main" className="flex flex-col gap-1">
            {links(true)}
          </nav>
        </DrawerContent>
      </Drawer>
      <main className="mx-auto w-full max-w-[1360px] flex-1 px-4 pb-16 pt-8 sm:px-8">{children}</main>
    </div>
  );
}
